import React, { useEffect, useRef, useState } from 'react';
import { ProjectId } from '../../../models/Types';
import { makePrioStyles } from '../../../theme/utils';
import {
  Typography,
  Select,
  Input,
  Collapse,
  Divider,
  notification,
} from 'antd';
import { useTranslation } from 'react-i18next';
import Flex from '../../../components/Flex';
import {
  apiFetchSettingOptions,
  apiSetSettingFromDropdown,
  apiUpdateProjectMailSetting,
} from '../api';
import { GlobalProjectSettingType } from '../../../models/Types';
import classNames from 'classnames';
import { Button, Tooltip } from '@prio365/prio365-react-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DocumentPrefixOptionsResponseDto } from '../../../models/Project';
import { ESettingScope, ESettingScopeType } from '../../../models/Settings';
import HTMLTextEditor from '../../../components/HTMLTextEditor';

import 'froala-editor/js/froala_editor.pkgd.min.js';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';

const SCOPE_ORDER: ESettingScopeType[] = [
  ESettingScope.GLOBAL,
  ESettingScope.OFFICE,
  ESettingScope.PROJECT,
];

const useStyles = makePrioStyles((theme) => ({
  root: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
    padding: theme.old.spacing.defaultPadding,
  },
  content: {
    marginBottom: theme.old.spacing.unit(1.5),
  },
  collapse: {
    '&.ant-collapse > .ant-collapse-item > .ant-collapse-header': {
      paddingLeft: 0,
      paddingRight: 0,
      display: 'flex',
      alignItems: 'center',
    },
    '&.ant-collapse-ghost > .ant-collapse-item > .ant-collapse-content > .ant-collapse-content-box':
      {
        padding: 0,
      },
  },
  divider: {
    '&.ant-divider-horizontal': {
      margin: 0,
      flex: 1,
      alignSelf: 'center',
      minWidth: 0,
    },
  },
  preview: {
    padding: theme.old.spacing.defaultPadding,
    border: theme.old.borders.sub,
    borderRadius: theme.old.borders.radius,
    backgroundColor: theme.old.palette.backgroundPalette.sub,
  },
  newValueRow: {
    marginTop: theme.old.spacing.unit(1),
    display: 'flex',
    gap: 8,
    width: '100%',
  },
  newValueRowEditor: {
    marginTop: theme.old.spacing.unit(1),
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
  },
  newValueRowButtons: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
  },
  newValueInput: {
    flex: 1,
  },
  ddGroupLabel: {
    padding: `${theme.old.spacing.unit(1)}px ${theme.old.spacing.unit(1.5)}px ${theme.old.spacing.unit(0.5)}px`,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: 'rgba(0,0,0,0.45)',
  },
  ddDivider: {
    borderTop: theme.old.borders.sub,
    margin: `${theme.old.spacing.unit(0.5)}px 0`,
  },
  ddOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: `${theme.old.spacing.unit(0.75)}px ${theme.old.spacing.unit(1.5)}px`,
    cursor: 'pointer',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.sub,
    },
  },
  ddOptionSelected: {
    backgroundColor: theme.old.palette.backgroundPalette.sub,
    fontWeight: 500,
  },
  ddOptionLabel: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 14,
  },
  ddCheckIcon: {
    flexShrink: 0,
    fontSize: 12,
    color: theme.old.palette.primaryColor,
  },
  selectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.old.spacing.unit(0.5),
  },
  selectFlex: {
    flex: 1,
  },
  previewLabel: {
    width: 100,
    display: 'flex',
    alignItems: 'center',
  },
}));

interface SingleStringSettingManagementProps {
  className?: string;
  projectId: ProjectId;
  settingKey: string;
  settingType: GlobalProjectSettingType;
  title: string;
  addNewLabel: string;
  newValuePlaceholder: string;
  previewType?: 'text' | 'html';
  inputType?: 'input' | 'textarea' | 'html-editor';
  /**
   * Optional: called after each options load/change to get the display-ready preview.
   * Useful when resolvedValue contains unresolved placeholders (e.g. document prefix).
   * If omitted, resolvedValue from the options API is shown directly.
   */
  resolvedPreviewFn?: () => Promise<string | null>;
}

export const SingleStringSettingManagement: React.FC<
  SingleStringSettingManagementProps
> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    className,
    projectId,
    settingKey,
    settingType,
    title,
    addNewLabel,
    newValuePlaceholder,
    previewType = 'text',
    inputType = 'input',
    resolvedPreviewFn,
  } = props;

  const [settingOptions, setSettingOptions] =
    useState<DocumentPrefixOptionsResponseDto | null>(null);
  const [selectedSettingId, setSelectedSettingId] = useState<string | null>(
    null
  );
  const [preview, setPreview] = useState<string | null>(null);
  const [showNewValueInput, setShowNewValueInput] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingSettingId, setEditingSettingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Keep the latest resolvedPreviewFn in a ref so the effect doesn't re-run when the inline function reference changes.
  const resolvedPreviewFnRef = useRef(resolvedPreviewFn);
  resolvedPreviewFnRef.current = resolvedPreviewFn;

  const updatePreview = async (fallbackValue: string) => {
    if (resolvedPreviewFnRef.current) {
      const resolved = await resolvedPreviewFnRef.current();
      setPreview(resolved);
    } else {
      setPreview(fallbackValue || null);
    }
  };

  const applyOptionsResponse = async (
    data: DocumentPrefixOptionsResponseDto
  ) => {
    setSettingOptions(data);
    setSelectedSettingId(data.selectedSettingId || null);
    await updatePreview(data.resolvedValue);
  };

  useEffect(() => {
    const load = async () => {
      const { data } = await apiFetchSettingOptions(projectId, settingKey);
      if (data) {
        await applyOptionsResponse(data);
      }
    };
    load();
    // projectId and settingKey are the only stable deps we care about here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, settingKey]);

  const handleSelectChange = async (value: string) => {
    setSelectedSettingId(value);
    const { data } = await apiSetSettingFromDropdown(projectId, settingKey, {
      action: 0,
      sourceSettingId: value,
    });
    if (data) {
      await applyOptionsResponse(data);
      notification.open({
        message: t('common:success'),
        description: t('common:successMessages.saveChange'),
      });
    } else {
      notification.open({
        message: t('common:error'),
        description: t('common:errorMessages.saveChangeError'),
      });
    }
  };

  const handleAddNew = async () => {
    if (!newValue.trim()) return;
    setSaving(true);
    const { data } = await apiSetSettingFromDropdown(projectId, settingKey, {
      action: 1,
      newValue: newValue.trim(),
    });
    setSaving(false);
    if (data) {
      await applyOptionsResponse(data);
      setNewValue('');
      setShowNewValueInput(false);
      notification.open({
        message: t('common:success'),
        description: t('common:successMessages.saveChange'),
      });
    } else {
      notification.open({
        message: t('common:error'),
        description: t('common:errorMessages.saveChangeError'),
      });
    }
  };

  const handleFormCancel = () => {
    setShowNewValueInput(false);
    setNewValue('');
    setEditingSettingId(null);
    setEditValue('');
  };

  const handleEditSave = async () => {
    if (editSaving || !editingSettingId || !editValue.trim()) return;
    if (editValue.trim() === selectedProjectOption?.value?.trim()) {
      handleFormCancel();
      return;
    }
    setEditSaving(true);
    const { data } = await apiUpdateProjectMailSetting(
      settingType,
      projectId,
      {
        projectSettingId: editingSettingId,
        globalProjectSettingId: editingSettingId,
        projectId,
        useProjectSetting: true,
        objectType: settingType,
        objectJson: { value: editValue.trim() },
      }
    );
    if (!data) {
      setEditSaving(false);
      notification.open({
        message: t('common:error'),
        description: t('common:errorMessages.saveChangeError'),
      });
      return;
    }
    // The deprecated PUT resets the selection — re-select the same setting via POST
    // so the settingOptions response reflects the correct selectedSettingId.
    const savedId = editingSettingId;
    setEditingSettingId(null);
    setEditValue('');
    const { data: reselected } = await apiSetSettingFromDropdown(
      projectId,
      settingKey,
      { action: 0, sourceSettingId: savedId }
    );
    if (reselected) {
      await applyOptionsResponse(reselected);
    } else {
      const { data: refreshed } = await apiFetchSettingOptions(
        projectId,
        settingKey
      );
      if (refreshed) await applyOptionsResponse(refreshed);
    }
    setEditSaving(false);
    setDropdownOpen(false);
    notification.open({
      message: t('common:success'),
      description: t('common:successMessages.saveChange'),
    });
  };

  const selectedProjectOption =
    settingOptions?.options?.find(
      (o) => o.settingId === selectedSettingId && o.scope === ESettingScope.PROJECT
    ) ?? null;

  const optionsByScopeInOrder = SCOPE_ORDER.map((scope) => ({
    scope,
    items: (settingOptions?.options ?? []).filter((o) => o.scope === scope),
  })).filter((group) => group.items.length > 0);

  const getOptionLabel = (displayName: string, value: string) => {
    const raw = displayName || value;
    if (previewType === 'html') {
      return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
    return raw;
  };

  return (
    <div className={classNames(classes.root, className)}>
      <Typography.Title>{title}</Typography.Title>

      <div className={classes.content}>
        <div className={classes.selectRow}>
          <Select
            className={classes.selectFlex}
            value={selectedSettingId ?? undefined}
            open={dropdownOpen}
            onDropdownVisibleChange={setDropdownOpen}
            loading={settingOptions === null}
            dropdownRender={() => (
              <div>
                {optionsByScopeInOrder.map(({ scope, items }, idx) => (
                  <div key={scope}>
                    {idx > 0 && <div className={classes.ddDivider} />}
                    <div className={classes.ddGroupLabel}>
                      {t(
                        `projects:projectDocumentSettingsManagement.scope.${scope}`
                      )}
                    </div>
                    {items.map((option) => (
                      <div
                        key={option.settingId}
                        className={classNames(classes.ddOption, {
                          [classes.ddOptionSelected]:
                            option.settingId === selectedSettingId,
                        })}
                      >
                        <div
                          className={classes.ddOptionLabel}
                          onClick={() => {
                            handleSelectChange(option.settingId);
                            setDropdownOpen(false);
                          }}
                        >
                          {getOptionLabel(option.displayName, option.value)}
                        </div>
                        {option.settingId === selectedSettingId && (
                          <FontAwesomeIcon
                            icon={['fal', 'check']}
                            className={classes.ddCheckIcon}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          >
            {(settingOptions?.options ?? []).map((option) => (
              <Select.Option key={option.settingId} value={option.settingId}>
                {getOptionLabel(option.displayName, option.value)}
              </Select.Option>
            ))}
          </Select>
          <Tooltip
            overlay={
              selectedProjectOption
                ? t('projects:projectDocumentSettingsManagement.edit')
                : t(
                    'projects:projectDocumentSettingsManagement.editDisabledTooltip'
                  )
            }
            placement="top"
          >
            <span style={{ display: 'inline-flex' }}>
              <Button
                type="default"
                iconProp={['fal', 'pen']}
                disabled={!selectedProjectOption}
                onClick={() => {
                  if (!selectedProjectOption) return;
                  setShowNewValueInput(false);
                  setNewValue('');
                  setEditingSettingId(selectedProjectOption.settingId);
                  setEditValue(selectedProjectOption.value);
                }}
              />
            </span>
          </Tooltip>
          <Tooltip overlay={addNewLabel} placement="top">
            <Button
              type="default"
              iconProp={['fal', 'plus']}
              onClick={() => {
                setEditingSettingId(null);
                setEditValue('');
                setShowNewValueInput(true);
              }}
            />
          </Tooltip>
        </div>

        {(showNewValueInput || editingSettingId) && (
          <div
            className={
              inputType === 'html-editor'
                ? classes.newValueRowEditor
                : classes.newValueRow
            }
          >
            {inputType === 'html-editor' ? (
              <HTMLTextEditor
                className={classes.newValueInput}
                value={editingSettingId ? editValue : newValue}
                onChange={editingSettingId ? setEditValue : setNewValue}
              />
            ) : inputType === 'textarea' ? (
              <Input.TextArea
                className={classes.newValueInput}
                value={editingSettingId ? editValue : newValue}
                onChange={(e) =>
                  editingSettingId
                    ? setEditValue(e.target.value)
                    : setNewValue(e.target.value)
                }
                placeholder={newValuePlaceholder}
                rows={4}
              />
            ) : (
              <Input
                className={classes.newValueInput}
                value={editingSettingId ? editValue : newValue}
                onChange={(e) =>
                  editingSettingId
                    ? setEditValue(e.target.value)
                    : setNewValue(e.target.value)
                }
                placeholder={newValuePlaceholder}
                onPressEnter={editingSettingId ? handleEditSave : handleAddNew}
                autoFocus
              />
            )}
            <div className={classes.newValueRowButtons}>
              <Button type="default" onClick={handleFormCancel}>
                {t('projects:projectDocumentSettingsManagement.cancel')}
              </Button>
              <Button
                type="primary"
                loading={editingSettingId ? editSaving : saving}
                onClick={editingSettingId ? handleEditSave : handleAddNew}
              >
                {t('projects:projectDocumentSettingsManagement.save')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <Collapse ghost className={classes.collapse} defaultActiveKey={['1']}>
        <Collapse.Panel
          header={
            <Flex.Row flex={1}>
              <div className={classes.previewLabel}>
                {t('projects:projectDocumentSettingsManagement.preview')}
              </div>
              <Divider className={classes.divider} />
            </Flex.Row>
          }
          key="1"
        >
          <div className={classes.preview}>
            {preview ? (
              previewType === 'html' ? (
                <FroalaEditorView model={preview} />
              ) : (
                <div>{preview}</div>
              )
            ) : null}
          </div>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export default SingleStringSettingManagement;
